import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import HistoryDetailSidebar from '../components/HistoryDetailSidebar';
import ProductSidebar from '../components/ProductSidebar';
import { getHistory } from '../services/api';
import './HistoryPage.css';

/**
 * HistoryPage Component
 * Displays paginated search history with ability to revisit past searches
 */
const HistoryPage = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 50;

  // Sidebar state
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductSidebarOpen, setIsProductSidebarOpen] = useState(false);

  /**
   * Load history data from API
   */
  const loadHistory = async (page = 0) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getHistory(page, itemsPerPage);
      setHistory(response.history || []);
      setTotalCount(response.total || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError(err.message || 'Failed to load search history');
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadHistory(0);
  }, []);

  /**
   * Handle clicking on a history item
   */
  const handleHistoryClick = (historyItem) => {
    setSelectedHistoryItem(historyItem);
    setIsHistorySidebarOpen(true);
  };

  /**
   * Handle closing history sidebar
   */
  const handleCloseHistorySidebar = () => {
    setIsHistorySidebarOpen(false);
    setTimeout(() => setSelectedHistoryItem(null), 300);
  };

  /**
   * Handle clicking on a product in the history sidebar
   */
  const handleProductClick = (product) => {
    // Convert history result format to product format
    const productData = {
      sku: product.sku,
      imageUrl: product.imageUrl,
      confidence: product.similarity || product.score || 0,
      category: product.category || 'Jewelry',
      metadata: {
        name: product.name,
        metal: product.metal,
        gemstone: product.gemstone,
        description: product.description,
      }
    };
    setSelectedProduct(productData);
    setIsProductSidebarOpen(true);
  };

  /**
   * Handle closing product sidebar
   */
  const handleCloseProductSidebar = () => {
    setIsProductSidebarOpen(false);
    setTimeout(() => setSelectedProduct(null), 300);
  };

  /**
   * Handle pagination
   */
  const handlePreviousPage = () => {
    if (currentPage > 0) {
      loadHistory(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const maxPage = Math.ceil(totalCount / itemsPerPage) - 1;
    if (currentPage < maxPage) {
      loadHistory(currentPage + 1);
    }
  };

  /**
   * Format date for display (compact version)
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    // Format as date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? '2-digit' : undefined
    });
  };

  /**
   * Get display image for history item
   * Uses searchedImageType to determine which image was actually searched
   */
  const getHistoryImage = (item) => {
    // Check if we have the searchedImageType field (new format)
    if (item.query?.searchedImageType) {
      const type = item.query.searchedImageType;

      if (type === 'original' && item.query?.originalImageUrl) {
        return item.query.originalImageUrl;
      }
      if (type === 'enhanced' && item.query?.enhancedImageUrl) {
        return item.query.enhancedImageUrl;
      }
      if (type === 'backgroundRemoved' && item.query?.backgroundRemovedImageUrl) {
        return item.query.backgroundRemovedImageUrl;
      }
    }

    // Fallback logic for old history entries without searchedImageType
    // Priority: background-removed > enhanced > original
    if (item.query?.backgroundRemovedImageUrl) {
      return item.query.backgroundRemovedImageUrl;
    }
    if (item.query?.enhancedImageUrl) {
      return item.query.enhancedImageUrl;
    }
    if (item.query?.originalImageUrl) {
      return item.query.originalImageUrl;
    }

    // Fallback to legacy imageUrl field (for very old history entries)
    if (item.query?.imageUrl) {
      return item.query.imageUrl;
    }

    // Last resort: use first result image
    if (item.results && item.results.length > 0 && item.results[0].imageUrl) {
      return item.results[0].imageUrl;
    }

    // Return placeholder
    return null;
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPreviousPage = currentPage > 0;

  return (
    <div className="history-page">
      <Header onHistoryClick={() => navigate('/')} />

      <main className="history-main">
        <div className="history-header">
          <button
            className="history-back-button"
            onClick={() => navigate('/')}
          >
            ← Back to Search
          </button>
          <h1 className="history-title">Search History</h1>
          <p className="history-subtitle">
            {totalCount > 0 ? `${totalCount} total search${totalCount !== 1 ? 'es' : ''}` : 'No searches yet'}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="history-loading">
            <div className="history-spinner"></div>
            <p>Loading search history...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="history-error">
            <span className="error-icon">⚠️</span>
            <p>{error}</p>
            <button
              className="retry-button"
              onClick={() => loadHistory(currentPage)}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && history.length === 0 && (
          <div className="history-empty">
            <img
              src="/assets/icon_clock.png"
              alt="No history"
              className="empty-icon"
            />
            <h2>No Search History</h2>
            <p>Your past searches will appear here</p>
            <button
              className="start-search-button"
              onClick={() => navigate('/')}
            >
              Start Searching
            </button>
          </div>
        )}

        {/* History Grid */}
        {!loading && !error && history.length > 0 && (
          <>
            <div className="history-grid">
              {history.map((item) => {
                const imageUrl = getHistoryImage(item);
                const resultsCount = item.metadata?.totalResults || item.results?.length || 0;

                return (
                  <div
                    key={item._id}
                    className="history-item"
                    onClick={() => handleHistoryClick(item)}
                  >
                    {/* Image Preview */}
                    <div className="history-item-image">
                      {imageUrl ? (
                        <img src={imageUrl} alt="Search preview" />
                      ) : (
                        <div className="history-item-placeholder"></div>
                      )}
                    </div>

                    {/* Item Details */}
                    <div className="history-item-details">
                      <div className="history-item-meta">
                        <span className="history-item-date">
                          {formatDate(item.createdAt)}
                        </span>
                      </div>

                      <div className="history-item-stats">
                        <span className="history-item-results">
                          {resultsCount} {resultsCount === 1 ? 'result' : 'results'}
                        </span>
                        {item.query?.filename && (
                          <span className="history-item-filename" title={item.query.filename}>
                            {item.query.filename.length > 18
                              ? item.query.filename.substring(0, 18) + '...'
                              : item.query.filename}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* View Arrow */}
                    <div className="history-item-arrow">→</div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="history-pagination">
                <button
                  className="pagination-button"
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage}
                >
                  ← Previous
                </button>

                <span className="pagination-info">
                  Page {currentPage + 1} of {totalPages}
                </span>

                <button
                  className="pagination-button"
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* History Detail Sidebar */}
      <HistoryDetailSidebar
        historyItem={selectedHistoryItem}
        isOpen={isHistorySidebarOpen}
        onClose={handleCloseHistorySidebar}
        onProductClick={handleProductClick}
      />

      {/* Product Detail Sidebar */}
      <ProductSidebar
        product={selectedProduct}
        isOpen={isProductSidebarOpen}
        onClose={handleCloseProductSidebar}
      />
    </div>
  );
};

export default HistoryPage;
